import { AxiosError } from "axios";
import { useGettext } from "vue3-gettext";
import { CategoryId } from "@/services/collection/categories";
import { KnownErrorCodes, ErrorType_obsolete } from "@/services/aiIdentification";
import type { ErrorCode, ErrorResponse } from "@/services/aiIdentification";

export type AiIdentificationErrorResult = { kind: "loginRequired" } | { kind: "error"; code: ErrorCode; message: string };

export function useAiIdentificationErrorParser() {
  const { $gettext } = useGettext();

  const genericMessage = () => $gettext("Něco se pokazilo a nepodařilo se určit, co je na fotografii. Můžeš to zkusit znovu s jinou fotkou, nebo si zkontroluj správný výběr kategorie.");

  const parsePokemon = (e: AxiosError): AiIdentificationErrorResult => {
    const status = e.response?.status;
    if (status === 403) return { kind: "loginRequired" };
    if (status === 406) {
      const error = e.response?.data as ErrorResponse;
      return { kind: "error", code: error.type, message: error.message };
    }
    return { kind: "error", code: KnownErrorCodes.SearchApiError, message: genericMessage() };
  };

  const parseSportsCards = (e: AxiosError): AiIdentificationErrorResult => {
    const status = e.response?.status;
    if (status === 403) return { kind: "loginRequired" };
    if (status === 422) {
      type ErrorResponseSportsCards = { error: ErrorType_obsolete; reasoning?: string };
      const response = e.response?.data as ErrorResponseSportsCards;
      const error = response.error;
      if (error === ErrorType_obsolete.ContentFilter) {
        return { kind: "error", code: KnownErrorCodes.VisionApiError, message: genericMessage() };
      } else if (error === ErrorType_obsolete.InsufficientQuota || error === ErrorType_obsolete.RateLimitReached) {
        return {
          kind: "error",
          code: KnownErrorCodes.VisionRateLimited,
          message: $gettext("Všichni chtějí vědět, kolik stojí jejích poklady. Omlouváme se, ale AI Vision je aktuálně přetížený. Pročti si na chvíli náš magazín a zkus to za pár minut znovu."),
        };
      }
    }
    if (status === 406) {
      return { kind: "error", code: KnownErrorCodes.SearchNotFound, message: genericMessage() };
    }
    return { kind: "error", code: KnownErrorCodes.SearchApiError, message: genericMessage() };
  };

  const parseError = (e: unknown, categoryId: CategoryId): AiIdentificationErrorResult => {
    if (!(e instanceof AxiosError)) throw e;
    if (categoryId === CategoryId.Pokemon) return parsePokemon(e);
    if (categoryId === CategoryId.SportsCards) return parseSportsCards(e);
    return { kind: "error", code: KnownErrorCodes.SearchApiError, message: genericMessage() };
  };

  return { parseError };
}
